import { useLiveBondRefresh } from "../../features/assetCatalogue/useLiveBondRefresh";
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Redirect,
  Route,
  Switch,
  useParams,
  useRouteMatch,
} from 'react-router-dom';

import MyHeader from './Header';
import { SessionPresenceProvider } from '../../components/SessionPresence';
import { useMonitoringReady } from '../../components/MonitoringObserver';
import Overview from './Overview/Overview';
import SessionSettings from './SessionSettings';
import SessionSettingsDense from './SessionSettingsDense';
import TradingIntervals from './TradingIntervals';
import Performance from './Performance';

import {
  selectors,
  connectToSessionSocket,
  disconnectFromSessionSocket,
  retrieveSession,
  fetchKlasses,
  fetchHistoricalStep,
  fetchMarketHours,
  HISTORICAL_SCENARIO,
} from 'state';
import { LoadingScreen } from 'components';
import MacroBackground from './MacroBackground';
import { TrainerSessionContextWrapper } from './TrainerSessionContext';
import CentralBank from './CentralBank';
import { fetchAuctions } from '../../state/app/auctions/actions';
import OverviewClientDealing from './Overview/OverviewClientDealing';
import PassingTimeBanner from '../../components/PassingTimeBanner';
import Analysis from './Analysis/analysis_index';
import AiAnalysis from './AI/ai_index';
import AnalysisStudioLive from './AnalysisStudioLive';
import Assessment from './Assessment';
import { ConfigProvider } from 'antd';
import { useTandem } from '../../features/tandem/TandemSessionBoundary';

const TrainerSession: React.FC = () => {
  const tandem = useTandem();
  const dispatch = useDispatch();
  const token = useSelector(selectors.token) as string;

  const { url } = useRouteMatch();
  const { id } = useParams<{ id: string }>();
  const sessionId = Number(id);
  useLiveBondRefresh(sessionId);

  const [loading, setLoading] = useState(true);
  const session = useSelector(selectors.activeSession);
  const klass = useSelector(selectors.klassById(session?.klass));
  useMonitoringReady(sessionId, !loading && session?.id === sessionId && !!klass, 'legacy');
  // Legacy Analysis tab is retired — never mount it, regardless of
  // show_analysis_tab; the /analysis route below always redirects to overview.
  // (Page code kept under Analysis/; its recompute buttons now live in Admin.)
  const canAccessAnalysis = false;
  // Live studio supports both historical and real-time sessions, so it's
  // gated purely on the admin toggle (no scenario_type constraint).
  const canAccessAnalysisStudio = session?.show_analysis_studio_tab;
  // console.log('id: ', id);
  useEffect(() => {
    dispatch(retrieveSession({ id: sessionId }));
    dispatch(fetchKlasses({}));
    dispatch(fetchHistoricalStep({}));
    dispatch(fetchAuctions({ session_id: sessionId }));
    dispatch(fetchMarketHours({}));

    // this will immediately be set to false without actually waiting on the requests to resolve, its useless
    setLoading(false);
  }, [dispatch, sessionId]);

  useEffect(() => {
    dispatch(connectToSessionSocket(token, id));

    return () => {
      dispatch(disconnectFromSessionSocket(token, id));
    };
  }, [dispatch, id, token, sessionId]);

  if (loading || !session || !klass) {
    return <LoadingScreen fullscreen />;
  }

  return (
    <SessionPresenceProvider sessionId={sessionId}>
    <TrainerSessionContextWrapper>
      <div>
        <MyHeader />
        {session.scenario_type === HISTORICAL_SCENARIO && (
          <PassingTimeBanner
            status={session.currently_passing_time}
            // delay={session.delay_minutes_left}
          />
        )}
        <div style={{ padding: '30px 70px 70px 70px' }}>
          <Switch>
            <Route path={`${url}/overview`}>
              {session.client_money ? <OverviewClientDealing /> : <Overview />}
              {/*{session.client_money ? <Overview /> : <OverviewClientDealing />}*/}
            </Route>
            {/* New default "Session Settings" (the dense Settings3 build) now
                lives at the canonical /session-settings URL. */}
            <Route path={`${url}/session-settings`}>
              <ConfigProvider componentDisabled={tandem ? true : undefined}><SessionSettingsDense /></ConfigProvider>
            </Route>
            {/* Old Session Settings — hidden from the navbar, kept reachable at
                /old-settings as a fallback in case of issues with the new tab. */}
            <Route path={`${url}/old-settings`}>
              <ConfigProvider componentDisabled={tandem ? true : undefined}><SessionSettings /></ConfigProvider>
            </Route>
            <Route path={`${url}/central-bank`}>
              <CentralBank />
            </Route>
            {/*<Route path={`${url}/benchmarks`}>*/}
            {/*  <Benchmarks />*/}
            {/*</Route>*/}
            <Route path={`${url}/trading-intervals`}>
              <TradingIntervals />
            </Route>
            <Route path={`${url}/performance`}>
              <Performance />
            </Route>
            <Route path={`${url}/assessment`}>
              <Assessment />
            </Route>
            {/* Live-wired studio (real session data). */}
            <Route path={`${url}/analysis-studio`}>
              {canAccessAnalysisStudio ? (
                <AnalysisStudioLive />
              ) : (
                <Redirect to={`${url}/overview`} />
              )}
            </Route>
            <Route path={`${url}/analysis`}>
              {canAccessAnalysis ? (
                <Analysis />
              ) : (
                <Redirect to={`${url}/overview`} />
              )}
            </Route>
            <Route path={`${url}/ai`}>
              <AiAnalysis />
            </Route>
            <Route path={`${url}/macro-background`}>
              <MacroBackground />
            </Route>
            {/*<Route path={`${url}/instruments`}>*/}
            {/*  <SessionInstrumentsSelector />*/}
            {/*</Route>*/}
            <Redirect to={`${url}/overview`} />
          </Switch>
        </div>
      </div>
    </TrainerSessionContextWrapper>
    </SessionPresenceProvider>
  );
};

export default TrainerSession;
